/**
 * ProviderListItem - Single row for a recognition provider in the voter list.
 *
 * Shows the provider label, training/auth status, and controls:
 * - Untrained: greyed out with "Not trained" badge; click navigates to /register
 * - Auth-gated + guest: disabled with "Login required" badge; click navigates to
 *   /login with register mode preselected
 * - Otherwise: Switch toggle + reorder chevrons
 */

import { ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { AvailableProvider } from '@dice-capture/shared/types/dice-registry';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useAuthStore } from '@/stores/authStore';
import { cn } from '@/lib/utils';

interface ProviderListItemProps {
  /** The provider to display */
  provider: AvailableProvider;
  /** Whether this provider is currently active */
  isActive: boolean;
  /** Whether this is the first item in the active list (disables move-up) */
  isFirst: boolean;
  /** Whether this is the last item in the active list (disables move-down) */
  isLast: boolean;
  /** Called when the toggle switch is changed */
  onToggle: () => void;
  /** Called when the move-up chevron is clicked */
  onMoveUp: () => void;
  /** Called when the move-down chevron is clicked */
  onMoveDown: () => void;
}

/**
 * Renders a single provider row with appropriate controls based on
 * training/auth state.
 */
export function ProviderListItem({
  provider,
  isActive,
  isFirst,
  isLast,
  onToggle,
  onMoveUp,
  onMoveDown
}: ProviderListItemProps) {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isUntrained = !provider.trained;
  const needsAuth = !!provider.requiresAuth && !isAuthenticated;
  // Auth gate only applies when the provider is trained — untrained takes precedence.
  const isAuthGated = needsAuth && !isUntrained;

  const handleRowClick = () => {
    if (isUntrained) {
      navigate('/register');
    } else if (isAuthGated) {
      navigate('/login', { state: { mode: 'register' } });
    }
  };

  const isClickable = isUntrained || isAuthGated;

  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-md border border-border/50 px-3 py-2',
        isClickable && 'cursor-pointer opacity-60 hover:opacity-80'
      )}
      onClick={isClickable ? handleRowClick : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={
        isClickable ? (e) => e.key === 'Enter' && handleRowClick() : undefined
      }
    >
      {/* Provider label */}
      <span className="flex-1 text-sm font-medium truncate">{provider.label}</span>

      {/* Badges */}
      {isUntrained && (
        <Badge variant="outline" className="text-xs shrink-0">
          Not trained
        </Badge>
      )}
      {isAuthGated && (
        <Badge variant="outline" className="text-xs shrink-0">
          Login required
        </Badge>
      )}

      {/* Controls for trained, non-gated providers */}
      {!isUntrained && !isAuthGated && (
        <>
          {/* Reorder chevrons only render when this row is active AND has at least one
              sibling to swap with. Otherwise the chevrons would be permanently disabled
              and read like duplicate toggle controls (issue spotted in TICKET-279 testing). */}
          {isActive && !(isFirst && isLast) && (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 shrink-0"
                disabled={isFirst}
                onClick={(e) => { e.stopPropagation(); onMoveUp(); }}
                aria-label={`Move ${provider.label} up`}
              >
                <ChevronUp className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 shrink-0"
                disabled={isLast}
                onClick={(e) => { e.stopPropagation(); onMoveDown(); }}
                aria-label={`Move ${provider.label} down`}
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
            </>
          )}
          <Switch
            checked={isActive}
            onCheckedChange={onToggle}
            aria-label={`Toggle ${provider.label}`}
          />
        </>
      )}
    </div>
  );
}
